import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Button, Form } from 'react-bootstrap';
import {
  FaArrowRight,
  FaCheckCircle,
  FaFileSignature,
  FaLock,
  FaShieldAlt,
} from 'react-icons/fa';
import { agreementAuthCode } from '../../../components/utilities/models/gen-models';
import axios from '../../../components/utilities/axios';
import LandingHeader from '../landing-page/landing-header/LandingHeader';
import './user-agreement.css';

const UserAgreement = () => {
  const [isChecked, setIsChecked] = useState(false);
  const [showCodeField, setShowCodeField] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('success');
  const [showAlert, setShowAlert] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: yupResolver(agreementAuthCode),
  });

  const handleChangeCheck = () => {
    const nextValue = !isChecked;
    setIsChecked(nextValue);
    setShowCodeField(nextValue);
    setShowAlert(false);
    setErrorMessage('');
  };

  const handleAuthUser = async (data) => {
    setProcessing(true);
    setShowAlert(false);
    setErrorMessage('');

    try {
      const response = await axios.post('/devices/agreement-authenticate', data);
      if (response.data?.success) {
        setAlertType('success');
        setAlertMessage(response.data.message || 'User authenticated successfully.');
        setShowAlert(true);
        reset();
        return;
      }

      setAlertType('danger');
      setAlertMessage(response.data?.message || 'Invalid verification code.');
      setShowAlert(true);
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message || 'We could not verify this account right now.',
      );
    } finally {
      setProcessing(false);
    }
  };

  return (
    <main className="agreement-page">
      <LandingHeader menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

      <section className="agreement-hero">
        <div className="agreement-hero__inner">
          <div className="agreement-hero__icon">
            <FaFileSignature />
          </div>
          <div>
            <span className="agreement-eyebrow">cLBS account access</span>
            <h1>User agreement &amp; verification</h1>
            <p>
              Review the terms that govern your account, then verify your details
              to continue securely.
            </p>
          </div>
        </div>
      </section>

      <div className="agreement-content">
        <div className="agreement-layout">
          <article className="agreement-card agreement-terms">
            <div className="agreement-card__header">
              <span className="agreement-eyebrow">Terms of use</span>
              <h2>Before you continue</h2>
              <p>
                These terms apply when you use cLBS services through our websites,
                mobile applications, and related platforms.
              </p>
            </div>

            <section className="agreement-section">
              <h3>Agreement and privacy</h3>
              <p>
                This user agreement applies to you as a user of cLBS-related
                services provided by Ontrac Technology Limited. We describe how
                we handle the information you provide and collect when you use our
                services.
              </p>
              <p>
                We may store, maintain, process, or transfer information in a
                country with different data protection laws. Where that happens,
                we take reasonable measures to protect your information.
              </p>
            </section>

            <section className="agreement-section">
              <h3>Disclaimer</h3>
              <p>
                By using cLBS products and services, you agree that this End User
                License Agreement is a binding contract and agree to follow the
                intellectual property laws and all terms stated here.
              </p>
            </section>

            <section className="agreement-section">
              <h3>Binding agreement</h3>
              <p>
                These terms form a binding agreement between you and cLBS. Your
                access to or use of the services indicates your acceptance of
                these terms. You agree to use the services at your own risk.
              </p>
            </section>

            <section className="agreement-section">
              <h3>User information and privacy</h3>
              <p>
                When you register, you provide information such as a valid phone
                number. You acknowledge that you have no ownership rights in an
                account created for you.
              </p>
              <p>
                User information is handled according to the cLBS Privacy Policy.
                We may contact users periodically, respond to legal requirements,
                or share information where permitted by law.
              </p>
            </section>

            <section className="agreement-section">
              <h3>Changes to these terms</h3>
              <p>
                cLBS may revise these terms at any time by updating this page.
                Changes are binding from the date they are posted. Continued use
                of the services means that you accept the current terms.
              </p>
            </section>

            <section className="agreement-section agreement-contact">
              <h3>Contact</h3>
              <p>
                Questions about these terms can be sent to the cLBS support team
                through your account administrator.
              </p>
            </section>
          </article>

          <aside className="agreement-aside">
            <div className="agreement-card agreement-verify-card">
              <div className="agreement-verify-card__icon">
                <FaShieldAlt />
              </div>
              <span className="agreement-eyebrow">Secure verification</span>
              <h2>Ready to continue?</h2>
              <p>
                Confirm that you have read the agreement, then enter your phone
                number and verification code.
              </p>

              <label className="agreement-check">
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={handleChangeCheck}
                />
                <span>
                  I have read and accept the cLBS user agreement and privacy terms.
                </span>
              </label>

              {showCodeField && (
                <Form onSubmit={handleSubmit(handleAuthUser)} className="agreement-form">
                  <Form.Group className="agreement-field">
                    <Form.Label>Phone number</Form.Label>
                    <Form.Control
                      type="tel"
                      placeholder="Enter your phone number"
                      isInvalid={Boolean(errors.phoneNumber)}
                      {...register('phoneNumber')}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.phoneNumber?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="agreement-field">
                    <Form.Label>Verification code</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter verification code"
                      isInvalid={Boolean(errors.agreeAuthCode)}
                      {...register('agreeAuthCode')}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.agreeAuthCode?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  {errorMessage && (
                    <Alert variant="danger" className="agreement-alert">
                      {errorMessage}
                    </Alert>
                  )}
                  {showAlert && (
                    <Alert
                      variant={alertType}
                      dismissible
                      onClose={() => setShowAlert(false)}
                      className="agreement-alert"
                    >
                      {alertMessage}
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    className="agreement-submit"
                    disabled={processing}
                  >
                    {processing ? (
                      <>
                        <span className="agreement-spinner" /> Verifying...
                      </>
                    ) : (
                      <>
                        Verify account <FaArrowRight />
                      </>
                    )}
                  </Button>
                </Form>
              )}

              <div className="agreement-privacy-note">
                <FaLock />
                <span>Your information is handled securely and never shared.</span>
              </div>
            </div>

            <div className="agreement-trust-card">
              <FaCheckCircle />
              <div>
                <strong>Protected access</strong>
                <span>Verification keeps your account and services secure.</span>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default UserAgreement;
